import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Mic, Square, Upload, Loader2, FileAudio, Pause, Play,
  Trash2, FileText, Radio
} from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '00:00';
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * InlineCallRecorder
 *
 * A compact recorder that captures speakerphone audio, ships it to
 * /api/calls-to-text/transcribe and (optionally) auto-attaches the result
 * to a Lead Nurture lead.
 *
 * Props:
 *  - leadId            (string|null)  : auto-attach to this lead id
 *  - leadName          (string|null)  : displayed in confirmations
 *  - authToken         (string)       : Bearer token
 *  - onTranscribed     (fn)           : callback(transcript) on success
 *  - compact           (bool)         : tighter layout for nested panels
 */
export default function InlineCallRecorder({
  leadId = null,
  leadName = '',
  authToken,
  onTranscribed,
  compact = false,
}) {
  const authHeaders = { Authorization: `Bearer ${authToken}` };

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [title, setTitle] = useState('');
  const [transcribing, setTranscribing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioUrlRef = useRef('');

  useEffect(() => { audioUrlRef.current = audioUrl; }, [audioUrl]);

  useEffect(() => {
    const timer = timerRef;
    const stream = streamRef;
    const url = audioUrlRef;
    return () => {
      if (timer.current) clearInterval(timer.current);
      if (stream.current) stream.current.getTracks().forEach(t => t.stop());
      if (url.current) URL.revokeObjectURL(url.current);
    };
  }, []);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Your browser does not support audio recording');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;

      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      };

      recorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
      toast.success('Recording started');
    } catch (e) {
      toast.error(e.message || 'Microphone access denied');
    }
  };

  const togglePause = () => {
    const r = mediaRecorderRef.current;
    if (!r) return;
    if (r.state === 'recording') {
      r.pause();
      setIsPaused(true);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    } else if (r.state === 'paused') {
      r.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setElapsed(prev => prev + 1), 1000);
    }
  };

  const stopRecording = () => {
    const r = mediaRecorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setIsRecording(false);
    setIsPaused(false);
  };

  const discard = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl('');
    setTitle('');
    setElapsed(0);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25MB Whisper limit'); return; }
    setAudioBlob(file);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(URL.createObjectURL(file));
    setTitle(file.name.replace(/\.[^.]+$/, ''));
    setElapsed(0);
  };

  const submit = async () => {
    if (!audioBlob) { toast.error('No audio to transcribe'); return; }
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = (audioBlob.type || '').includes('webm') ? 'webm' : ((audioBlob.name || '').split('.').pop() || 'webm');
      const filename = audioBlob.name ? audioBlob.name : `recording_${Date.now()}.${ext}`;
      fd.append('file', audioBlob, filename);
      const finalTitle = title.trim() || (leadName ? `Call with ${leadName}` : '');
      if (finalTitle) fd.append('title', finalTitle);
      if (leadId) fd.append('lead_id', leadId);
      if (elapsed > 0) fd.append('duration_seconds', String(elapsed));

      const r = await fetch(`${backendUrl}/api/calls-to-text/transcribe`, {
        method: 'POST',
        headers: authHeaders,
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Transcription failed');

      toast.success(leadId ? `Transcript logged on ${leadName || 'lead'}` : 'Transcript ready');
      discard();
      if (onTranscribed) onTranscribed(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTranscribing(false);
    }
  };

  const padding = compact ? 'p-3' : 'p-4';

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-slate-700/40 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 ${padding}`}
      data-testid="inline-call-recorder"
    >
      {/* glow */}
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl pointer-events-none transition-opacity ${isRecording && !isPaused ? 'bg-rose-500/40 opacity-60' : 'bg-cyan-500/20 opacity-30'}`}></div>

      <div className="relative flex items-center gap-3 mb-3">
        <div className={`relative w-9 h-9 rounded-lg flex items-center justify-center text-white shadow ${isRecording && !isPaused ? 'bg-gradient-to-br from-rose-500 to-red-600' : 'bg-gradient-to-br from-cyan-500 to-blue-600'}`}>
          <Radio className={`w-4 h-4 ${isRecording && !isPaused ? 'animate-pulse' : ''}`} />
          {isRecording && !isPaused && (
            <span className="absolute inset-0 rounded-lg ring-2 ring-rose-400/50 animate-ping"></span>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">Record this call</p>
          <p className="text-xs text-slate-400 truncate">
            {leadName ? `Auto-attached to ${leadName}` : 'Whisper transcription powered by AI'}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="relative flex flex-wrap items-center gap-2">
        {!isRecording && !audioBlob && (
          <>
            <Button
              type="button"
              size="sm"
              onClick={startRecording}
              className="bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white shadow-lg shadow-rose-500/20"
              data-testid="inline-start-recording-btn"
            >
              <Mic className="w-4 h-4 mr-1.5" /> Start
            </Button>
            <label className="inline-flex">
              <input type="file" accept="audio/*" className="hidden" onChange={handleFileUpload} data-testid="inline-upload-audio-input" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-600 text-xs text-slate-200 hover:bg-slate-800 cursor-pointer transition">
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload audio
              </span>
            </label>
          </>
        )}

        {isRecording && (
          <>
            <Button type="button" size="sm" onClick={togglePause} variant="outline" className="border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700" data-testid="inline-pause-recording-btn">
              {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
            <Button type="button" size="sm" onClick={stopRecording} className="bg-slate-100 text-slate-900 hover:bg-white" data-testid="inline-stop-recording-btn">
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
          </>
        )}

        <span className="ml-auto inline-flex items-center gap-2 text-xs">
          {isRecording && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-300">
              <span className={`w-1.5 h-1.5 rounded-full ${isPaused ? 'bg-amber-400' : 'bg-rose-400 animate-pulse'}`}></span>
              {isPaused ? 'Paused' : 'LIVE'}
            </span>
          )}
          <span className="font-mono text-cyan-300" data-testid="inline-elapsed-timer">{formatDuration(elapsed)}</span>
        </span>
      </div>

      {/* Live waveform-style indicator while recording */}
      {isRecording && !isPaused && (
        <div className="relative mt-3 flex items-end gap-1 h-6" aria-hidden="true">
          {[...Array(28)].map((_, i) => (
            <span
              key={i}
              className="flex-1 rounded-sm bg-gradient-to-t from-rose-500/40 to-cyan-400/80 animate-pulse"
              style={{ animationDelay: `${(i % 7) * 80}ms`, height: `${20 + ((i * 11) % 70)}%` }}
            />
          ))}
        </div>
      )}

      {/* Preview + submit */}
      {audioBlob && !isRecording && (
        <div className="relative mt-3 space-y-2 border-t border-slate-700/40 pt-3" data-testid="inline-audio-preview">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder={leadName ? `Title (default: Call with ${leadName})` : 'Title'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-500"
              data-testid="inline-recording-title-input"
            />
            <span className="text-xs text-slate-400 self-center whitespace-nowrap">
              <FileAudio className="w-3.5 h-3.5 inline mr-1" /> {formatBytes(audioBlob.size)}
            </span>
          </div>
          {audioUrl && <audio controls src={audioUrl} className="w-full" data-testid="inline-audio-player" />}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              onClick={submit}
              disabled={transcribing}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              data-testid="inline-transcribe-btn"
            >
              {transcribing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              {transcribing ? 'Transcribing…' : 'Transcribe & Save'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={discard} disabled={transcribing} className="border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700" data-testid="inline-discard-recording-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Discard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
